/**
 * PauseWorkDialog Component
 *
 * Dialog for pausing a work order with options:
 * - Reason (optional)
 * - If lift assigned: choose to stay on lift or move to parking
 * - If moving to parking: select parking spot
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DialogContent,
  TextField,
  Stack,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Typography,
  Box,
} from '@mui/material';
import { FormDialog } from '@/components/common/FormDialog';
import { ParkingSpotSelector } from './ParkingSpotSelector';
import { usePauseWorkOrder, usePauseService } from '@/hooks/useWorkOrders';
import { useNotify } from '@/stores/notificationStore';

interface PauseWorkDialogProps {
  open: boolean;
  workOrderId: string;
  hasAssignedLift: boolean;
  onClose: () => void;
  /**
   * When set, this dialog pauses a single service (the last active one, which
   * tips the whole work order into paused) and captures the continuation plan
   * for the work order (#548). The lift/parking choice is a work-order-level
   * concern and is hidden in this mode.
   */
  bookingServiceId?: string;
}

export function PauseWorkDialog({
  open,
  workOrderId,
  hasAssignedLift,
  onClose,
  bookingServiceId,
}: PauseWorkDialogProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const pauseMutation = usePauseWorkOrder();
  const pauseServiceMutation = usePauseService();

  const [reason, setReason] = useState('');
  const [pauseLocation, setPauseLocation] = useState<'lift' | 'parking'>('lift');
  const [parkingZoneId, setParkingZoneId] = useState('');
  const [parkingSpotId, setParkingSpotId] = useState('');
  // Continuation plan (#548): expected resume time, or "reschedule later" (no
  // plan) — captured so paused work is not operationally ambiguous.
  const [expectedResumeAt, setExpectedResumeAt] = useState('');
  const [rescheduleLater, setRescheduleLater] = useState(false);

  const handleSubmit = async () => {
    // null = "reschedule later" / no continuation plan.
    const resumeIso =
      rescheduleLater || !expectedResumeAt ? null : new Date(expectedResumeAt).toISOString();
    try {
      if (bookingServiceId) {
        // Pausing the last active service tips the work order into paused; the
        // backend persists this continuation plan on the work order (#548).
        await pauseServiceMutation.mutateAsync({
          workOrderId,
          serviceId: bookingServiceId,
          expectedResumeAt: resumeIso,
        });
      } else {
        await pauseMutation.mutateAsync({
          id: workOrderId,
          reason: reason.trim() || undefined,
          pauseLocation: hasAssignedLift ? pauseLocation : undefined,
          parkingZoneId: pauseLocation === 'parking' && parkingZoneId ? parkingZoneId : undefined,
          parkingSpotId: pauseLocation === 'parking' && parkingSpotId ? parkingSpotId : undefined,
          expectedResumeAt: resumeIso,
        });
      }
      onClose();
      // Reset form
      setReason('');
      setPauseLocation('lift');
      setParkingZoneId('');
      setParkingSpotId('');
      setExpectedResumeAt('');
      setRescheduleLater(false);
    } catch {
      notify.error(tc('error'), t('pauseDialog.pauseFailed'));
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={t('pauseDialog.title')}
      isDirty={true}
      onSubmit={handleSubmit}
      isLoading={pauseMutation.isPending || pauseServiceMutation.isPending}
      submitLabel={t('pauseDialog.pause')}
      maxWidth="sm"
    >
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <TextField
            label={t('pauseDialog.reason')}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('pauseDialog.reasonPlaceholder')}
            multiline
            rows={2}
            fullWidth
          />

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              {t('pauseDialog.continuationTitle')}
            </Typography>
            <TextField
              label={t('pauseDialog.expectedResume')}
              type="datetime-local"
              value={expectedResumeAt}
              onChange={(e) => setExpectedResumeAt(e.target.value)}
              disabled={rescheduleLater}
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
              helperText={t('pauseDialog.expectedResumeHint')}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={rescheduleLater}
                  onChange={(e) => setRescheduleLater(e.target.checked)}
                />
              }
              label={t('pauseDialog.rescheduleLater')}
              sx={{ mt: 0.5 }}
            />
          </Box>

          {!bookingServiceId && hasAssignedLift && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {t('pauseDialog.locationTitle')}
              </Typography>
              <RadioGroup
                value={pauseLocation}
                onChange={(e) => setPauseLocation(e.target.value as 'lift' | 'parking')}
              >
                <FormControlLabel
                  value="lift"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {t('pauseDialog.stayOnLift')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('pauseDialog.stayOnLiftDesc')}
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="parking"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {t('pauseDialog.moveToParking')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('pauseDialog.moveToParkingDesc')}
                      </Typography>
                    </Box>
                  }
                />
              </RadioGroup>

              {pauseLocation === 'parking' && (
                <Box sx={{ mt: 2, ml: 4 }}>
                  <ParkingSpotSelector
                    selectedZoneId={parkingZoneId}
                    selectedSpotId={parkingSpotId}
                    onZoneChange={setParkingZoneId}
                    onSpotChange={setParkingSpotId}
                  />
                </Box>
              )}
            </Box>
          )}
        </Stack>
      </DialogContent>
    </FormDialog>
  );
}
